import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { useModal } from '../../context/ModalContext';
import { useContent } from '../../context/ContentContext';
import {
  Target, Lightbulb, Users, Award, Star,
  MapPin, Phone, Mail, Clock, ArrowRight, ExternalLink,
} from 'lucide-react';
import { getMenuConfig } from '../../lib/menuConfig';

const VALUE_ICON_MAP = { target: Target, lightbulb: Lightbulb, users: Users, award: Award, star: Star };

const parseLines = (str) =>
  (str || '').split('\n').map((s) => s.trim()).filter(Boolean);

/* ─── Products overview (full-image gallery) ────────────────────────────── */
const ProductsOverview = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { openModal } = useModal();
  const { siteContent } = useContent();
  const isRtl = language === 'ar';

  const rawCategories = siteContent?.products?.categories || [];
  const categories = rawCategories
    .filter(c => c.enabled !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const cols = siteContent?.products?.popup_columns || 3;
  const gridClass = cols === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 sm:grid-cols-3';

  const getTitle = (cat) => language === 'ar' ? cat.title_ar : cat.title_en;
  const getDesc  = (cat) => language === 'ar' ? cat.description_ar : cat.description_en;

  if (!categories.length) return (
    <div className="p-8 text-center text-white/30 text-sm">No products configured.</div>
  );

  return (
    <div className={isRtl ? 'text-right' : 'text-left'}>
      {/* Header */}
      <div className={`px-6 pt-6 pb-4 border-b ${isDark ? 'border-white/8' : 'border-gray-100'}`}>
        <p className="text-[var(--gold-primary)] uppercase tracking-[0.2em] text-xs font-semibold mb-1">
          {t('products.overline')}
        </p>
        <h2 className={`font-display text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <span className="gold-text">{t('products.title')}</span>
        </h2>
        <p className={`text-xs mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
          {t('products.subtitle')}
        </p>
      </div>

      {/* Gallery grid */}
      <div className={`p-4 grid ${gridClass} gap-3`}>
        {categories.map((cat) => (
          <button
            key={cat.id || cat.key}
            onClick={() => openModal('product', {
              key: cat.key || cat.id,
              title: getTitle(cat),
              image_url: cat.image_url,
              content: language === 'ar' ? cat.modal_content_ar : cat.modal_content_en,
              badge: language === 'ar' ? cat.badge_ar : cat.badge_en,
              tagline: language === 'ar' ? cat.tagline_ar : cat.tagline_en,
              features: parseLines(language === 'ar' ? cat.features_ar : cat.features_en),
              variants: parseLines(language === 'ar' ? cat.variants_ar : cat.variants_en),
              origins: parseLines(language === 'ar' ? cat.origins_ar : cat.origins_en),
            })}
            className="group relative overflow-hidden rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-primary)]"
            data-testid={`section-product-${cat.key || cat.id}`}
          >
            {/* Image — uniform height */}
            <div className="relative h-[480px] overflow-hidden">
              {cat.image_url ? (
                <img
                  src={cat.image_url}
                  alt={getTitle(cat)}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              ) : (
                <div className={`w-full h-full ${isDark ? 'bg-[#1a1a1a]' : 'bg-gray-200'}`} />
              )}

              {/* Gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

              {/* Gold bottom border */}
              <div className="edge-underline absolute bottom-0 h-0.5 w-0 bg-[var(--gold-primary)] group-hover:w-full transition-all duration-500" />

              {/* Text overlay */}
              <div className={`absolute bottom-0 left-0 right-0 p-3 ${isRtl ? 'text-right' : 'text-left'}`}>
                {cat.featured && (
                  <span className="inline-block mb-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-[var(--gold-primary)] text-black rounded-[2px]">
                    {language === 'ar' ? 'مميز' : 'Featured'}
                  </span>
                )}
                <h3 className="font-display text-sm sm:text-base font-bold text-white leading-snug line-clamp-2 group-hover:text-[var(--gold-primary)] transition-colors duration-200">
                  {getTitle(cat)}
                </h3>
                <p className="text-white/60 text-xs mt-0.5 line-clamp-1 hidden sm:block">
                  {getDesc(cat)}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Footer hint */}
      <p className={`px-6 pb-4 text-xs text-center ${isDark ? 'text-white/25' : 'text-gray-300'}`}>
        {language === 'ar' ? 'انقر على أي منتج للاطلاع على تفاصيله' : 'Click any product to view full details'}
      </p>
    </div>
  );
};

/* ─── About overview ─────────────────────────────────────────────────────── */
const AboutOverview = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { openModal } = useModal();
  const { siteContent } = useContent();
  const isRtl = language === 'ar';

  const values = (siteContent?.about?.values || [])
    .filter(v => v.enabled !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const stats = [
    { value: t('about.stats.employees'), label: t('about.stats.employeesLabel') },
    { value: t('about.stats.experience'), label: t('about.stats.experienceLabel') },
    { value: t('about.stats.markets'), label: t('about.stats.marketsLabel') },
  ].filter(s => s.value && s.value !== 'about.stats.employees' && s.value !== 'about.stats.experience' && s.value !== 'about.stats.markets');

  const description1 = t('about.description1');
  const description2 = t('about.description2');
  const valuesHeading = t('about.valuesHeading');

  return (
    <div className={`p-6 sm:p-8 ${isRtl ? 'text-right' : 'text-left'}`}>
      <p className="text-[var(--gold-primary)] uppercase tracking-[0.2em] text-xs font-semibold mb-2">{t('about.overline')}</p>
      <h2 className={`font-display text-2xl sm:text-3xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>
        <span className="gold-text">{t('about.title')}</span>
      </h2>

      {stats.length > 0 && (
        <div className={`grid grid-cols-3 gap-3 mb-8 p-4 rounded-sm ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-100'}`}>
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <p className="font-display text-2xl font-bold gold-text">{s.value}</p>
              <p className={`text-[10px] uppercase tracking-wider mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-400'}`}>{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {description1 && description1 !== 'about.description1' && (
        <p className={`text-sm leading-relaxed mb-4 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>{description1}</p>
      )}
      {description2 && description2 !== 'about.description2' && (
        <p className={`text-sm leading-relaxed mb-8 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>{description2}</p>
      )}

      {values.length > 0 && (
        <>
          <h3 className={`text-xs uppercase tracking-widest font-semibold text-[var(--gold-primary)] mb-4`}>
            {valuesHeading && valuesHeading !== 'about.valuesHeading'
              ? valuesHeading
              : (language === 'ar' ? 'قيمنا الأساسية' : 'Our Core Values')}
          </h3>

          <div className="grid sm:grid-cols-3 gap-3">
            {values.map((v, i) => {
              const Icon = VALUE_ICON_MAP[v.icon] || Target;
              const title = language === 'ar' ? v.title_ar : v.title_en;
              const description = language === 'ar' ? v.description_ar : v.description_en;
              return (
                <button
                  key={v.id || i}
                  onClick={() => openModal('about-value', { key: v.id, title, description })}
                  className={`group p-5 rounded-sm relative overflow-hidden transition-all duration-300 border ${isRtl ? 'text-right' : 'text-left'} ${
                    isDark
                      ? 'bg-[#1A1A1A] border-white/10 hover:border-[var(--gold-primary)]/50'
                      : 'bg-gray-50 border-gray-200 hover:border-[var(--gold-primary)]/50'
                  }`}
                  data-testid={`section-value-${v.id || i}`}
                >
                  <span className={`absolute top-3 ${isRtl ? 'left-3' : 'right-3'} font-display text-3xl font-bold ${isDark ? 'text-white/5' : 'text-gray-100'} group-hover:text-[var(--gold-primary)]/10 transition-colors`}>
                    0{i + 1}
                  </span>
                  <div className={`w-9 h-9 flex items-center justify-center rounded-sm mb-3 transition-colors ${isDark ? 'bg-[var(--gold-primary)]/15 group-hover:bg-[var(--gold-primary)]/25' : 'bg-[var(--gold-primary)]/10 group-hover:bg-[var(--gold-primary)]/20'}`}>
                    <Icon className="w-4 h-4 text-[var(--gold-primary)]" />
                  </div>
                  <h4 className={`font-display text-sm font-semibold mb-1.5 group-hover:text-[var(--gold-primary)] transition-colors ${isDark ? 'text-white' : 'text-gray-800'}`}>{title}</h4>
                  <p className={`text-xs leading-relaxed ${isDark ? 'text-white/55' : 'text-gray-500'}`}>{description}</p>
                  <div className="edge-underline absolute bottom-0 w-0 h-0.5 bg-[var(--gold-primary)] group-hover:w-full transition-all duration-500" />
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

/* ─── Contact overview ────────────────────────────────────────────────────── */
const ContactOverview = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { openModal } = useModal();
  const isRtl = language === 'ar';

  const cards = [
    { key: 'office', icon: MapPin, title: t('contact.office.title'), lines: [t('contact.office.address'), t('contact.office.city')] },
    { key: 'phone', icon: Phone, title: t('contact.phone.title'), lines: [t('contact.phone.number')] },
    { key: 'email', icon: Mail, title: t('contact.email.title'), lines: [t('contact.email.address')] },
    { key: 'hours', icon: Clock, title: t('contact.hours.title'), lines: [t('contact.hours.weekdays'), t('contact.hours.weekend')] },
  ];

  return (
    <div className={`p-6 sm:p-8 ${isRtl ? 'text-right' : 'text-left'}`}>
      <p className="text-[var(--gold-primary)] uppercase tracking-[0.2em] text-xs font-semibold mb-2">{t('contact.overline')}</p>
      <h2 className={`font-display text-2xl sm:text-3xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
        <span className="gold-text">{t('contact.title')}</span>
      </h2>
      <p className={`text-sm mb-8 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{t('contact.subtitle')}</p>

      <div className="grid sm:grid-cols-2 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              onClick={() => openModal('contact', { tab: c.key })}
              className={`group relative overflow-hidden p-5 rounded-sm transition-all duration-300 border ${isRtl ? 'text-right' : 'text-left'} ${
                isDark
                  ? 'bg-[#1A1A1A] border-white/10 hover:border-[var(--gold-primary)]/50'
                  : 'bg-gray-50 border-gray-200 hover:border-[var(--gold-primary)]/50'
              }`}
              data-testid={`section-contact-${c.key}`}
            >
              <div className={`w-9 h-9 flex items-center justify-center rounded-sm mb-3 transition-colors ${isDark ? 'bg-[var(--gold-primary)]/15 group-hover:bg-[var(--gold-primary)]/25' : 'bg-[var(--gold-primary)]/10 group-hover:bg-[var(--gold-primary)]/20'}`}>
                <Icon className="w-4 h-4 text-[var(--gold-primary)]" />
              </div>
              <h4 className={`font-display text-sm font-semibold mb-2 group-hover:text-[var(--gold-primary)] transition-colors ${isDark ? 'text-white' : 'text-gray-800'}`}>{c.title}</h4>
              {c.lines.map((line, i) => (
                <p key={i} className={`text-xs ${isDark ? 'text-white/55' : 'text-gray-500'}`}>{line}</p>
              ))}
              <div className="edge-underline absolute bottom-0 w-0 h-0.5 bg-[var(--gold-primary)] group-hover:w-full transition-all duration-500" />
            </button>
          );
        })}
      </div>

      {/* Quick email CTA */}
      <div className={`mt-6 p-5 rounded-sm text-center ${isDark ? 'bg-[var(--gold-primary)]/10 border border-[var(--gold-primary)]/20' : 'bg-[var(--gold-primary)]/5 border border-[var(--gold-primary)]/20'}`}>
        <p className={`text-sm mb-3 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
          {language === 'ar' ? 'فريقنا جاهز للإجابة على استفساراتكم' : 'Our team is ready to answer your inquiries'}
        </p>
        <button
          onClick={() => openModal('contact', { tab: 'email' })}
          className="btn-shine inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--gold-primary)] text-black font-semibold text-xs uppercase tracking-wider rounded-sm hover:bg-[var(--gold-hover)] transition-all"
        >
          <Mail className="w-3.5 h-3.5" />
          {language === 'ar' ? 'أرسل رسالة الآن' : 'Send a Message Now'}
        </button>
      </div>
    </div>
  );
};

/* ─── Social redirect ─────────────────────────────────────────────────────── */
const SocialRedirectModal = ({ data }) => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const isRtl = language === 'ar';
  const { closeModal } = useModal();

  const labels = {
    en: { title: 'Leaving Our Website', desc: 'You are about to visit an external social media page.', confirm: 'Continue', cancel: 'Stay Here' },
    ar: { title: 'مغادرة موقعنا', desc: 'أنت على وشك زيارة صفحة تواصل اجتماعي خارجية.', confirm: 'متابعة', cancel: 'البقاء هنا' },
  };
  const l = labels[language];

  return (
    <div className={`p-8 text-center ${isRtl ? 'text-right' : 'text-left'}`}>
      <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 bg-[var(--gold-primary)]/10">
        <ExternalLink className="w-6 h-6 text-[var(--gold-primary)]" />
      </div>
      <h2 className={`font-display text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>{l.title}</h2>
      <p className={`text-sm mb-6 max-w-xs mx-auto ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{l.desc}</p>
      <div className={`flex items-center justify-center gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
        <button
          onClick={closeModal}
          className={`px-5 py-2.5 border rounded-sm text-sm font-semibold transition-colors ${isDark ? 'border-white/20 text-white/70 hover:border-white/40' : 'border-gray-300 text-gray-600 hover:border-gray-400'}`}
        >
          {l.cancel}
        </button>
        <a
          href={data?.url || '#'}
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeModal}
          className="btn-shine px-5 py-2.5 bg-[var(--gold-primary)] text-black font-semibold text-sm rounded-sm hover:bg-[var(--gold-hover)] transition-all"
        >
          {l.confirm}
        </a>
      </div>
    </div>
  );
};

/* ─── Main SectionModal dispatcher ──────────────────────────────────────── */
const SectionModal = ({ data }) => {
  const { siteContent } = useContent();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const section = data?.section;
  const menuConfig = getMenuConfig(siteContent?.global?.menu);

  if (menuConfig[section] && menuConfig[section]?.enabled === false) {
    return (
      <div className="p-8 text-center">
        <h2 className={`font-display text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
          {language === 'ar' ? 'هذا القسم مخفي حالياً' : 'This section is currently hidden'}
        </h2>
        <p className={`${isDark ? 'text-white/60' : 'text-gray-500'}`}>
          {language === 'ar'
            ? 'يمكنك إظهاره مرة أخرى من لوحة التحكم.'
            : 'You can show it again from the admin dashboard.'}
        </p>
      </div>
    );
  }

  if (section === 'products') return <ProductsOverview />;
  if (section === 'about') return <AboutOverview />;
  if (section === 'contact') return <ContactOverview />;
  if (section === 'social') return <SocialRedirectModal data={data} />;
  return null;
};

export default SectionModal;
